import { NamirasoftAccessServer } from "namirasoft-access";
import { NamirasoftAccountServer } from "namirasoft-account";
import { FieldCacheService, NSARouterMaker, NSARouterMakerResult, NSFRouterMaker } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { EnvService, IStorageSession } from "namirasoft-core";
import { NSNotFoundPage } from "namirasoft-site-react";
import { useReducer } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AccountLayout } from "./AccountLayout";
import { AccountRoutes } from "./AccountRoutes";
import GithubCallbackPage from "./pages/GithubCallbackPage";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import LogoutPage from "./pages/LogoutPage";
import NotificationSettingPage from "./pages/NotificationSettingPage";
import PasswordChangePage from "./pages/PasswordChangePage";
import PasswordForgetPage from "./pages/PasswordForgetPage";
import PasswordNewPage from "./pages/PasswordNewPage";
import RegisterPage from "./pages/RegisterPage";
import ServiceSessionEditPage from "./pages/ServiceSessionEditPage";
import ServiceSessionListPage from "./pages/ServiceSessionListPage";
import ServiceSessionViewPage from "./pages/ServiceSessionViewPage";
import ServiceUserEditPage from "./pages/ServiceUserEditPage";
import ServiceUserListPage from "./pages/ServiceUserListPage";
import ServiceUserViewPage from "./pages/ServiceUserViewPage";
import SessionListPage from "./pages/SessionListPage";
import SessionViewDecidePage from "./pages/SessionViewDecidePage";
import SessionViewPage from "./pages/SessionViewPage";
import TokenViewPage from "./pages/TokenViewPage";
import UserBannedPage from "./pages/UserBannedPage";
import UserDeletePage from "./pages/UserDeletePage";
import UserProfilePage from "./pages/UserProfilePage";
import UserViewDecidePage from "./pages/UserViewDecidePage";
import UserViewPage from "./pages/UserViewPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyPhonePage from "./pages/VerifyPhonePage";

function Router()
{
    let [, page_force_update] = useReducer((x) => x + 1, 0);

    let navigate = useNavigate();

    return <NSARouterMaker
        product_id={ProductUUID.NAMIRASOFT_ACCOUNT}
        notifier_storage={new IStorageSession()}
        page_force_update={page_force_update}
        navigate={navigate}
        renderOnLogin={(result: NSARouterMakerResult) =>
        {
            let REACT_APP_BASE_URL_ACCOUNT = new EnvService("REACT_APP_BASE_URL_ACCOUNT", true).getString();
            let REACT_APP_BASE_URL_ACCESS = new EnvService("REACT_APP_BASE_URL_ACCESS", true).getString();

            let account = new NamirasoftAccountServer(REACT_APP_BASE_URL_ACCOUNT, result.account.token_manager, result.notifier.onError);
            let access = new NamirasoftAccessServer(REACT_APP_BASE_URL_ACCESS, result.account.token_manager, result.notifier.onError);

            let servers = { account, access };

            return (
                <FieldCacheService
                    {...result}
                    render={(rows => (
                        <Routes>
                            <Route path={AccountRoutes.Home} element={<HomePage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.Logout} element={<LogoutPage {...result} servers={servers} />} />

                            <Route path={AccountRoutes.PasswordChange} element={<PasswordChangePage {...result} servers={servers} />} />

                            <Route path={AccountRoutes.VerifyPhone} element={<VerifyPhonePage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.VerifyEmail} element={<VerifyEmailPage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.Notification} element={<NotificationSettingPage {...result} servers={servers} />} />

                            <Route path={AccountRoutes.UserProfile} element={<UserProfilePage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.UserDelete} element={<UserDeletePage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.UserViewDecide(":id")} element={<UserViewDecidePage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.UserView(":id")} element={<UserViewPage {...result} servers={servers} />} />

                            <Route path={AccountRoutes.SessionList} element={<SessionListPage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.SessionViewDecide(":id")} element={<SessionViewDecidePage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.SessionView(":id")} element={<SessionViewPage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.SessionSign(":id")} element={<TokenViewPage {...result} servers={servers} GetToken={result.account.server.session.GetToken} />} />

                            <Route path={AccountRoutes.ServiceUserList} element={<ServiceUserListPage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.ServiceUserNew} element={<ServiceUserEditPage {...result} servers={servers} isEdit={false} />} />
                            <Route path={AccountRoutes.ServiceUserCopy(":id")} element={<ServiceUserEditPage {...result} servers={servers} isEdit={false} />} />
                            <Route path={AccountRoutes.ServiceUserEdit(":id")} element={<ServiceUserEditPage {...result} servers={servers} isEdit={true} />} />
                            <Route path={AccountRoutes.ServiceUserView(":id")} element={<ServiceUserViewPage {...result} servers={servers} />} />

                            <Route path={AccountRoutes.ServiceSessionList} element={<ServiceSessionListPage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.ServiceSessionNew} element={<ServiceSessionEditPage {...result} servers={servers} isEdit={false} />} />
                            <Route path={AccountRoutes.ServiceSessionCopy(":id")} element={<ServiceSessionEditPage {...result} servers={servers} isEdit={false} />} />
                            <Route path={AccountRoutes.ServiceSessionView(":id")} element={<ServiceSessionViewPage {...result} servers={servers} />} />
                            <Route path={AccountRoutes.ServiceSessionSign(":id")} element={<TokenViewPage {...result} servers={servers} GetToken={result.account.server.service_session.GetToken} />} />

                            <Route path={AccountRoutes.GithubOauthCallback} element={<GithubCallbackPage />} />

                            {NSFRouterMaker.getRoutes({ ...result, rows, children: <></> })}

                            <Route path="*" element={<NSNotFoundPage />} />
                        </Routes>
                    ))}
                />
            );
        }}
        renderOnLogout={(result: NSARouterMakerResult, banned: boolean) =>
        {
            if (banned)
                return (
                    <Routes>
                        <Route path={AccountRoutes.Home} element={<UserBannedPage {...result} />} />
                        <Route path={AccountRoutes.UserBanned} element={<UserBannedPage {...result} />} />
                        <Route path="*" element={<LoginPage {...result} />} />
                    </Routes>
                );
            return (
                <Routes>
                    <Route path={AccountRoutes.Home} element={<LoginPage {...result} />} />
                    <Route path={AccountRoutes.Login} element={<LoginPage {...result} />} />
                    <Route path={AccountRoutes.Logout} element={<LogoutPage {...result} />} />
                    <Route path={AccountRoutes.Register} element={<RegisterPage {...result} />} />
                    <Route path={AccountRoutes.PasswordNew} element={<PasswordNewPage {...result} />} />
                    <Route path={AccountRoutes.PasswordForget} element={<PasswordForgetPage {...result} />} />
                    <Route path={AccountRoutes.GithubOauthCallback} element={<GithubCallbackPage />} />
                    <Route path="*" element={<LoginPage {...result} />} />
                </Routes>
            );
        }}
        getNSALayout={(props) =>
        {
            return (
                <AccountLayout {...props}>
                    {props.children}
                </AccountLayout>
            );
        }}
        getCustomRender={(result: NSARouterMakerResult) =>
        {
            if (window.location.pathname === AccountRoutes.Logout)
                return <LogoutPage {...result} />;
            return null;
        }}
        requirement={{
            verification: {
                email: false,
                phone: false
            }
        }}
    />;
}

export default Router;